import { ClientError } from 'graphql-request';

type GraphQLErrorShape = {
  message?: string;
  code?: string;
  extensions?: { code?: string };
};

type ErrorWithResponse = Error & {
  response?: { errors?: GraphQLErrorShape[] };
};

const INTERNAL_CODE = 'INTERNAL_SERVER_ERROR';

// Curated map of backend error keys → user-facing Spanish messages.
// Only keys that surface in UI mutations are listed; unknown keys fall back.
const FRIENDLY_ERROR_MESSAGES: Record<string, string> = {
  // Banlists
  banlist_not_found: 'No encontramos la banlist.',
  banlist_previous_format_mismatch:
    'La lista anterior tiene que ser del mismo formato.',
  banlist_previous_is_self: 'Una banlist no puede ser su propia lista anterior.',
  banlist_previous_not_found: 'No encontramos la lista anterior elegida.',
  // Auth / permissions
  not_authorized: 'No tenés permisos para realizar esta acción.',
  invalid_permissions: 'No tenés permisos para realizar esta acción.',
  insufficient_permissions: 'No tenés permisos para realizar esta acción.',
  unauthorized: 'Sesión expirada. Volvé a iniciar sesión.',
  invalid_token: 'Sesión inválida. Volvé a iniciar sesión.',
  token_expired: 'Sesión expirada. Volvé a iniciar sesión.',

  // Tournament lifecycle
  tournament_not_found: 'No encontramos el torneo.',
  invalid_dueling_book_tourney:
    'El ID de Dueling Book es el número que va después de tourney?id= en la URL del torneo.',
  invalid_platform: 'La plataforma elegida no existe. Elegí otra.',
  tournament_field_locked:
    'Ese dato ya no se puede cambiar en el estado actual del torneo. Recargá la pantalla para ver la configuración vigente.',
  tournament_full: 'El torneo ya alcanzó el máximo de jugadores.',
  double_elimination_no_teams:
    'La doble eliminación todavía no se puede jugar por equipos.',
  double_elimination_top_too_small:
    'El top en doble eliminación necesita al menos 4 clasificados.',
  invalid_top_type: 'El top sólo puede ser eliminación directa o doble.',
  tournament_has_no_organizer: 'El torneo no tiene un organizador asignado.',
  inscription_closed: 'Las inscripciones del torneo están cerradas.',
  inscription_not_open_yet: 'Las inscripciones todavía no están abiertas.',
  online_registration_not_allowed:
    'Este torneo no permite inscripción online.',
  round_not_active: 'La ronda no está activa.',
  round_not_current: 'Esa ronda ya no está en curso.',
  round_has_pending_matches: 'Todavía hay partidas sin reportar en la ronda.',
  round_deadline_passed: 'El tiempo de la ronda ya terminó.',
  no_previous_round: 'No hay ronda anterior para retroceder.',
  pairings_already_exist_for_round: 'Ya existen pairings para esta ronda.',
  cannot_reset_validated_tournament:
    'No se puede resetear un torneo ya validado.',
  cannot_rewind_validated_tournament:
    'No se puede retroceder un torneo ya validado.',
  only_admin_can_rewind_round: 'No tenés permisos para retroceder rondas en este torneo.',
  could_not_calculate_tiebreaker: 'No se pudieron calcular los desempates.',

  // Matches
  match_not_found: 'Partida no encontrada.',
  match_already_finished: 'Esa mesa ya tiene resultado.',
  match_is_bye: 'Un bye no tiene resultado que corregir.',
  invalid_winner: 'El ganador tiene que ser uno de los dos jugadores de la mesa.',
  public_event_result_locked: 'El evento ya terminó: corregilo desde la vista completa.',
  public_event_next_match_played:
    'La mesa siguiente ya se jugó con este resultado: corregí esa primero.',
  public_event_round_closed: 'Esa ronda ya se cerró: corregilo desde la vista completa.',
  match_already_reported: 'La partida ya fue reportada.',
  match_not_reported: 'La partida todavía no fue reportada.',
  match_already_exists: 'Ya existe una partida cargada.',
  match_not_in_current_round: 'La partida no pertenece a la ronda actual.',
  match_proof_required: 'Hace falta adjuntar la evidencia de la partida.',
  invalid_game_number: 'Número de game inválido.',
  invalid_winner_team: 'Equipo ganador inválido.',
  game3_not_allowed: 'No está permitido jugar un tercer game.',
  best_of_must_be_1_3_or_5: 'El best-of debe ser 1, 3 o 5.',
  all_individual_matches_must_be_finished:
    'Todas las partidas individuales deben estar finalizadas.',
  game_reporting_enforced:
    'Este torneo exige reportar el ganador de cada game individualmente.',

  // Players
  player_not_found: 'Jugador no encontrado.',
  player_already_exists: 'Ese jugador ya existe.',
  player_already_enrolled: 'El jugador ya está inscripto.',
  bye_match_not_available:
    'Esa mesa ya no tiene un jugador esperando rival. Actualizá y elegí otra.',
  late_entry_current_round_conflict:
    'Si lo sentás en una mesa con BYE juega la ronda actual: no le cargues un resultado para esa ronda.',
  late_entry_no_table_available:
    'No hay una mesa con un solo jugador en esta ronda donde sentarlo. Esperá a que se arme una (un bye o una mesa sin rival) o sumalo en la ronda siguiente.',
  late_entry_next_match_played:
    'El jugador de esa mesa ya jugó la ronda siguiente: no se puede reabrir. Probá con otra mesa.',
  player_already_accepted: 'El jugador ya fue aceptado.',

  // Public events (side events de un YCS)
  public_event_queue_duplicate: 'Ya está anotado en esta cola.',
  public_event_queue_entry_not_waiting: 'Ese jugador ya no está esperando en la cola.',
  public_event_full: 'El evento está lleno.',
  public_event_not_open: 'El evento ya no acepta inscripciones.',
  public_event_not_finished: 'El evento todavía no terminó.',
  public_event_not_enough_players: 'No hay jugadores suficientes para arrancar el evento.',
  public_event_judge_not_in_roster: 'Ese juez no está en el equipo de publics del evento.',
  public_event_type_not_on_demand: 'Esa modalidad no es on demand.',
  public_event_type_not_scheduled: 'Esa modalidad no es programada.',
  public_event_players_to_fire_invalid:
    'El cupo para disparar tiene que ser mayor o igual al mínimo de jugadores.',
  public_event_min_players_invalid: 'El mínimo de jugadores es 2.',
  public_event_max_players_invalid: 'El máximo no puede ser menor que el mínimo.',
  public_event_prize_invalid: 'Revisá los premios: posiciones y texto.',
  public_event_type_name_required: 'La modalidad necesita un nombre.',
  public_event_clone_same_event: 'Elegí otro evento para clonar el catálogo.',
  public_event_not_a_main_event: 'Un public event no puede tener su propio catálogo.',
  public_events_leader_only: 'Sólo el organizador o el head judge del evento puede hacer esto.',
  public_events_staff_only: 'Tenés que ser juez del evento para hacer esto.',
  player_already_dropped: 'El jugador ya fue dropeado.',
  player_not_dropped: 'El jugador no está dropeado.',
  player_not_enrolled: 'El jugador no está inscripto.',
  player_not_in_tournament: 'El jugador no está en este torneo.',
  already_enrolled: 'Ya estás inscripto en este torneo.',
  not_enrolled: 'No estás inscripto en este torneo.',
  // Revisión de listas (`reviewDecklistSubmission`): el motivo del rechazo
  // tiene tope de 1000 caracteres, y no se revisa lo que no se entregó.
  too_long: 'El motivo es demasiado largo.',
  no_submission: 'El jugador todavía no entregó la lista.',
  // Con `submissionCount`, la entrega que se revisaba ya no es la vigente.
  stale: 'El jugador subió otra lista mientras la revisabas. Mirala de nuevo.',
  new_player_already_in_tournament:
    'El jugador nuevo ya está en el torneo.',
  fixed_table_already_assigned:
    'La mesa fija ya está asignada a otro jugador.',

  // Teams
  team_is_full: 'El equipo ya está completo.',
  team_already_dropped: 'El equipo ya fue dropeado.',
  not_a_team_tournament: 'Este torneo no es por equipos.',
  at_least_one_team_required: 'Hace falta al menos un equipo.',
  teams_must_have_all_players: 'Los equipos deben tener todos los jugadores.',
  cannot_delete_team_with_matches:
    'No se puede eliminar un equipo con partidas cargadas.',
  cannot_update_team_players_with_matches:
    'No se pueden cambiar los jugadores de un equipo con partidas cargadas.',

  // Lectura por visión (capturas del KTS). El API manda estas claves en vez del
  // cuerpo crudo del proveedor, que llegaba en inglés y arrastraba el dump de
  // graphql-request —con la captura entera en base64— hasta la pantalla.
  vision_overloaded:
    'El modelo de visión está saturado en este momento. Esperá un par de minutos y volvé a intentar.',
  vision_rate_limited:
    'Se agotó la cuota de lecturas por visión por ahora. Probá de nuevo más tarde.',
  vision_empty_response:
    'El modelo de visión no devolvió nada para esa captura. Probá con una captura más nítida o recortada a la tabla.',
  vision_failed:
    'La lectura por visión falló. Probá de nuevo; si vuelve a fallar, completá a mano lo que falta.',

  // Decks
  deck_empty: 'El mazo está vacío.',
  deck_too_large: 'El mazo supera el tamaño máximo.',
  main_deck_too_large: 'El main deck supera el tamaño máximo.',
  main_deck_too_small: 'El main deck no tiene el tamaño mínimo.',
  extra_deck_too_large: 'El extra deck supera el tamaño máximo.',
  side_deck_too_large: 'El side deck supera el tamaño máximo.',
  cannot_merge_same_deck: 'No se puede mergear un mazo con sí mismo.',

  // Catálogo
  card_set_version_exists:
    'Ya existe esa versión de la carta (mismo código, rareza y nombre de versión). Ponele un nombre de versión distinto, por ejemplo "Extended Art".',
  card_set_version_deleted:
    'Esa versión de la carta existe pero está borrada. Restaurala o ponele un nombre de versión distinto.',

  // Marketplace
  card_already_in_wishlist: 'La carta ya está en tu wishlist.',
  invalid_price: 'Precio inválido.',
  invalid_quantity: 'Cantidad inválida.',
  invalid_rating: 'Puntaje inválido.',
  quantity_exceeds_available: 'La cantidad supera el stock disponible.',
  listing_not_active: 'La publicación no está activa.',
  listing_not_paused: 'La publicación no está pausada.',
  bundle_not_active: 'El bundle no está activo.',
  bundle_not_paused: 'El bundle no está pausado.',
  bundle_must_have_items: 'El bundle debe tener items.',
  deck_listing_not_published: 'La publicación del mazo no está publicada.',
  deck_listing_not_paused: 'La publicación del mazo no está pausada.',
  items_from_different_sellers:
    'No se pueden combinar items de vendedores distintos.',
  order_not_pending: 'La orden no está pendiente.',
  order_not_paid: 'La orden no está paga.',
  order_not_shipped: 'La orden no fue enviada.',
  order_not_confirmed: 'La orden no está confirmada.',
  order_not_delivered: 'La orden no fue entregada.',
  order_not_in_updatable_state:
    'La orden no se puede actualizar en este estado.',
  order_cannot_be_cancelled: 'La orden no se puede cancelar.',
  order_not_manual: 'Sólo se puede editar un pedido cargado a mano.',
  not_admin: 'Sólo un admin puede hacer esto.',
  invalid_override_status: 'No se puede mover el pedido a ese estado.',
  order_cancelled_stock_released:
    'El pedido se canceló y su stock volvió al inventario: no se puede reactivar.',
  buyer_review_already_submitted: 'Ya dejaste una reseña para esta compra.',
  review_already_submitted: 'Ya dejaste una reseña.',

  // User / profile
  email_already_validated: 'El email ya está validado.',
  email_mismatch: 'El email no coincide.',
  temporary_email: 'Ese email no es válido para esta acción.',
  'Email inválido': 'Email inválido.',
  'El email debe tener menos de 254 caracteres':
    'El email debe tener menos de 254 caracteres.',
  'Konami ID inválido': 'Konami ID inválido.',

  // Judges
  not_a_tournament_staff: 'No sos parte del staff de este torneo.',
  head_judge_required: 'Sólo el Head Judge puede hacer esto.',
  invalid_penalty_type: 'Ese tipo de penalidad no es válido.',
  invalid_match: 'La mesa no pertenece a este torneo.',
  player_not_in_match: 'El jugador no juega en esa mesa.',
  player_required: 'Elegí el jugador o escribí su nombre.',
  registered_player_required:
    'Match Loss y DQ sólo se aplican a un jugador inscripto.',
  invalid_round_or_table: 'La ronda y la mesa tienen que ser 1 o más.',
  deck_check_already_in_progress: 'Esa mesa ya tiene un deck check en curso.',
  deck_check_already_completed: 'Ese deck check ya se completó.',
  judge_call_not_pending: 'Esa llamada ya la tomó otro juez.',
  judge_call_not_active: 'Esa llamada ya no está activa.',

  // Generic
  BAD_REQUEST: 'La solicitud no es válida.',
  NOT_FOUND: 'No encontramos el recurso solicitado.',
};

const isSnakeCaseKey = (value: string) => /^[a-z][a-z0-9_]*$/.test(value);

// Algunos códigos vienen parametrizados: `order_dates_out_of_order:pago:envío`.
// El detalle es lo que hace útil el mensaje —cuál de las seis fechas quedó mal—
// así que la plantilla se busca por el prefijo y el resto se interpola. Sin
// esto la clave entera no matchea y el usuario ve el fallback genérico.
const PARAMETERIZED_ERROR_MESSAGES: Record<
  string,
  (args: string[]) => string
> = {
  invalid_order_date: ([field]) => `La fecha de ${field} no es válida.`,
  order_date_in_future: ([field]) =>
    `La fecha de ${field} no puede estar en el futuro.`,
  order_dates_out_of_order: ([field, previous]) =>
    `La fecha de ${field} no puede ser anterior a la de ${previous}.`,
  // price_changed:<listingId>=<precio>,… — los carritos lo leen con
  // parsePriceChangedError para actualizar los precios; esto es el texto genérico.
  price_changed: () =>
    'Cambió el precio de una o más cartas. Revisá el carrito y volvé a confirmar el pedido.',
};

// Detects raw/unsafe strings we don't want to show to users:
// Prisma errors, stack traces, multi-line dumps, etc.
const looksLikeRawError = (value: string) => {
  if (!value) return true;
  if (value.includes('\n')) return true;
  // Un cuerpo serializado, no una oración. Es lo que llega cuando un proveedor
  // externo contesta un error y su body sube tal cual: `{"error":{"code":503,
  // "message":"This model is currently experiencing high demand…"}}`.
  if (/^\s*[[{]/.test(value)) return true;
  if (value.includes('Invalid `prisma.')) return true;
  if (value.toLowerCase().includes('unique constraint')) return true;
  if (value.toLowerCase().includes('prisma.')) return true;
  return false;
};

// El API manda el código en la raíz del error (`{ message, code }`), no adentro
// de `extensions`. Se miran los dos: `extensions.code` es la forma estándar de
// GraphQL y la usan los errores que no arma nuestro formatter.
const errorCodeOf = (error: GraphQLErrorShape | undefined): string | null =>
  error?.code ?? error?.extensions?.code ?? null;

/**
 * ¿El API dijo "esto no existe"?
 *
 * Sirve para separar un 404 real de un API caído, que es la diferencia entre
 * mostrar la página de "no encontrado" y mostrar la página degradada. Sin esta
 * distinción un hipo del API le devuelve 404 a Google para un recurso que
 * existe, y eso desindexa.
 */
export function isNotFoundError(error: unknown): boolean {
  const { code, key } = extractErrorInfo(error);
  return code === 'NOT_FOUND' || key.endsWith('_not_found');
}

export function extractErrorInfo(error: unknown): {
  code: string | null;
  key: string;
} {
  if (!error || typeof error !== 'object') {
    return { code: null, key: '' };
  }

  if (error instanceof ClientError) {
    const first = error.response?.errors?.[0] as GraphQLErrorShape | undefined;
    return {
      code: errorCodeOf(first),
      key: first?.message ?? error.message ?? '',
    };
  }

  const err = error as ErrorWithResponse;
  const first = err.response?.errors?.[0];
  if (first) {
    return {
      code: errorCodeOf(first),
      key: first.message ?? err.message ?? '',
    };
  }

  return { code: null, key: err.message ?? '' };
}

/**
 * Returns a user-facing error message. Falls back to `fallback` when the
 * underlying error is a server-side crash, a raw Prisma error, or an unknown
 * snake_case code we haven't mapped yet.
 *
 * Natural-language messages already in Spanish (containing spaces) are
 * returned as-is after the raw-string guard.
 */
export function getFriendlyErrorMessage(
  error: unknown,
  fallback: string
): string {
  const { code, key } = extractErrorInfo(error);

  if (code === INTERNAL_CODE) return fallback;
  if (looksLikeRawError(key)) return fallback;

  const known = knownMessage(key);
  if (known) return known;

  const [prefix] = key.split(':');

  // Unknown snake_case key → not safe to show. Se mira el prefijo y no la clave
  // entera: si no, un código parametrizado sin plantilla se le escaparía crudo
  // al usuario, porque los dos puntos no matchean contra el patrón snake_case.
  if (isSnakeCaseKey(prefix ?? key)) return fallback;

  return key || fallback;
}

/** El mensaje mapeado (fijo o parametrizado) de un código, si lo tenemos. */
function knownMessage(key: string): string | null {
  if (FRIENDLY_ERROR_MESSAGES[key]) return FRIENDLY_ERROR_MESSAGES[key];
  const [prefix, ...args] = key.split(':');
  const template = prefix ? PARAMETERIZED_ERROR_MESSAGES[prefix] : undefined;
  return template ? template(args) : null;
}

/**
 * Como `getFriendlyErrorMessage`, pero más estricto: sólo devuelve mensajes
 * de códigos mapeados. Cualquier otro error —incluido un texto ya legible,
 * como el de un `Error` de red— cae en `fallback`.
 */
export function getKnownErrorMessage(error: unknown, fallback: string): string {
  return knownMessage(extractErrorInfo(error).key) ?? fallback;
}
